import {Middleware} from "redux";
import {externalClient} from "utils/clients";
import {handleSingleAction} from "utils/actions"
import {validate} from "modules/integrations/validator";
import {getPreviewPrefix, isPreviewMode} from "utils/previewMode";

const getUrlPrefix = () => {
    // If running locally as integrated MFE, use local proxy
    if (import.meta.env.VITE_IS_INTEGRATED_LOCALLY) {
        return "http://localhost:8888";
    }
    // Per-PR quick-change preview: the shell is served from
    // /previews/pr-<n>/index.html, so services.json and per-MFE
    // workload-config.json must be fetched from that prefix too —
    // otherwise we load the LIVE staging configs and the preview
    // boots the live MFE bundles instead of the PR-built ones.
    if (isPreviewMode()) {
        return getPreviewPrefix();
    }
    // If running as integrated MFE, use the configured WLM frontend URL
    return import.meta.env.VITE_IS_APP_INTEGRATED_MFE ? import.meta.env.VITE_WLM_FRONTEND_URL : "" ;
}

const appMiddleware: Middleware = ({dispatch}) => next => async action => {
    const {type, payload} = action;
    const response = next(action);

    if (type === "APP/BOOT") {
        dispatch({
            type: "APP/BOOT:SUCCESS"
        });
        dispatch({
            type: "AUTH/INITIALIZE",
            payload: {
                domain: import.meta.env.VITE_AUTH0_DOMAIN,
                clientId: import.meta.env.VITE_AUTH0_CLIENT,
                audience: import.meta.env.VITE_AUTH0_AUDIENCE
            }
        });
        dispatch({
            type: "APP/LOAD-WORKLOADS"
        });
    } else if (type === "APP/MINI-BOOT") {
        dispatch({
            type: "APP/MINI-BOOT:SUCCESS"
        });
        dispatch({
            type: "APP/LOAD-WORKLOADS"
        });
    } else if (type === "APP/BOOT:FAILED") {
        dispatch({
            type: "NOTIFICATIONS:ERROR",
            payload: {
                message: "Failed to initialize UI.",
                exception: payload,
                isFatal: true
            }
        })
    } else if (type === "APP/RELOAD") {
        window.location.reload();
    } else if (type === "APP/LOAD-WORKLOADS") {
        return handleSingleAction(action, async () => {
            const urlPrefix = getUrlPrefix();
            const {data: { services: workloads }} = await externalClient.get(`${urlPrefix}/services/services.json`);

            const allWorkloads = await Promise.all(workloads.map(async (workload: any) => {
                try {
                    const { data: config } = await externalClient.get(`${urlPrefix}${import.meta.env.DEV ? "/workloads" : "" }/${workload.prefix}workload-config.json`)

                    const validationResult = validate(config);

                    if(validationResult.success) {
                        return {...validationResult.data, prefix:workload.prefix}
                    } else {
                        console.error(`Config file for workload with prefix, ${workload.prefix}, and id: ${config?.id} failed validation`, validationResult.error)
                        return null;
                    }
                } catch (e) {
                    console.error(`Failed downloading workload config file with prefix, ${workload.prefix}. Workload will be dropped from UI.`, e)
                    return null;
                }
            }))

            return allWorkloads.filter(config => !!config);
        }).catch(e => {
            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    message: "Failed to download workload list (services.json). UI unable to render.",
                    exception: e,
                    isFatal: true
                }
            })
            throw e;
        });
    }

    return response;
}
export default appMiddleware;